import { Suspense, useEffect } from 'react'
import { ScrollView, StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'zens-router'
import { useLocalSearchParams } from 'expo-router'
import { useBuildings, useFloors } from 'zens-store'
import { Color, Radius, Spacing, Text } from 'zens-storybook'
import { FloorSelect } from 'zens-fragment'

import { BuildingSelect, LayoutHeader, MenuMore, ZoneSelect } from '@dms/component'
import { useBreadcrumb, usePermissions } from '@dms/hook'
import CareFloorplan from '@dms/component/care/floorplan'
import useFloorZoneIds from '@dms/component/care/use-floor-zone-ids'

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 0 },
  scroll: { flex: 1 },
  content: { flexGrow: 1, padding: Spacing.L },
  controls: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', zIndex: 100 },
  filters: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', minWidth: 0 },
  select: { minWidth: 180, marginRight: Spacing.S, marginBottom: Spacing.S },
  block: { flexGrow: 1, backgroundColor: Color.simplyWhite, borderRadius: Radius.M },
})

// Care filters belong to this page; global params can include IDs from older parent routes.
const useCareParams = () => {
  const localParams = useLocalSearchParams()
  const router = useRouter()
  const params = Object.fromEntries(
    Object.entries(localParams).map(([key, value]) => [key, parseInt(String(value), 10) || 0]),
  ) as Record<string, number>
  const setParams = (values: Record<string, number>) =>
    router.setParams(Object.fromEntries(Object.entries(values).map(([key, value]) => [key, String(value)])))
  return [params, setParams] as const
}

// Floor-dependent queries stay below local loading boundaries so the page controls remain visible.
const CareZoneFilter = () => {
  const { t } = useTranslation()
  const [{ companyId, floorId = 0, zoneId = 0 }, setParams] = useCareParams()
  const zoneIds = useFloorZoneIds(companyId, floorId)
  const selectedZoneId = zoneIds.includes(zoneId) ? zoneId : 0

  useEffect(() => {
    if (floorId && zoneId && !zoneIds.includes(zoneId)) setParams({ zoneId: 0 })
  }, [floorId, zoneId, zoneIds])

  return (
    <ZoneSelect
      companyId={companyId}
      floorId={floorId}
      zoneIds={zoneIds}
      value={selectedZoneId}
      style={styles.select}
      extraItems={[{ value: 0, title: t('All workspaces') }]}
      onChange={(value) => setParams({ zoneId: value })}
    />
  )
}

const CareSelectedFloorplan = () => {
  const [{ companyId, floorId = 0, zoneId = 0, rotation = 0 }] = useCareParams()
  const zoneIds = useFloorZoneIds(companyId, floorId)
  
  return (
    <CareFloorplan
      companyId={companyId}
      floorId={floorId}
      zoneId={zoneIds.includes(zoneId) ? zoneId : 0}
      rotation={rotation as 0 | 90 | 180 | 270}
    />
  )
}

const CareLiveContent = () => {
  const { t } = useTranslation()
  const [{ companyId, buildingId = 0, floorId = 0, rotation = 0 }, setParams] = useCareParams()
  const buildings = useBuildings({ companyId })
  const floors = useFloors({ companyId, buildingId }, { enabled: !!buildingId })
  const breadcrumb = useBreadcrumb([
    { title: t('Care'), href: `/companies/${companyId}/care/live` },
    { title: t('Live status') },
  ])

  useEffect(() => {
    if (!buildingId && buildings.data?.length) setParams({ buildingId: buildings.data[0].id, floorId: 0 })
  }, [buildingId, buildings.data])

  useEffect(() => {
    if (!floorId && floors.data?.length) setParams({ floorId: floors.data[0].id })
  }, [floorId, floors.data])

  return (
    <View style={styles.container}>
      <LayoutHeader title={t('Live status')} breadcrumb={breadcrumb} />
      <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
        <View style={styles.controls}>
          <View style={styles.filters}>
            <BuildingSelect
              companyId={companyId}
              groupLocation
              value={buildingId}
              style={styles.select}
              onChange={(value) => setParams({ buildingId: value, floorId: 0, zoneId: 0 })}
            />
            <FloorSelect
              companyId={companyId}
              buildingId={buildingId}
              value={floorId}
              style={styles.select}
              onChange={(value) => setParams({ floorId: value as number })}
            />
            <Suspense
              fallback={
                <View style={styles.select} testID="CareLive.ZonesLoading">
                  <Text.Skeleton />
                </View>
              }
            >
              <CareZoneFilter />
            </Suspense>
          </View>
          <MenuMore
            testID="CareLive.More"
            items={[
              { key: 'left', title: t('Rotate 90° left'), icon: 'gear', testID: 'CareLive.RotateLeft' },
              { key: 'right', title: t('Rotate 90° right'), icon: 'gear', testID: 'CareLive.RotateRight' },
            ]}
            onSelect={(key) => setParams({ rotation: (rotation + (key === 'left' ? 270 : 90)) % 360 })}
          />
        </View>
        <View style={styles.block}>
          <Suspense
            fallback={
              <View testID="CareLive.FloorplanLoading">
                <Text.Skeleton />
              </View>
            }
          >
            <CareSelectedFloorplan />
          </Suspense>
        </View>
      </ScrollView>
    </View>
  )
}

const CareLive = () => {
  const [{ companyId }] = useCareParams()
  const { isSysAdmin } = usePermissions(companyId)
  const router = useRouter()
  useEffect(() => {
    if (!isSysAdmin) router.replace(`/companies/${companyId}`)
  }, [companyId, isSysAdmin, router])

  if (!isSysAdmin) return null
  return (
    <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
      <CareLiveContent />
    </Suspense>
  )
}

export default CareLive
